// Abu Dhabi guide: who pays for AC maintenance. Written separately from the
// Dubai guide - different emirate, different law and dispute body.
const content = {
    slug: 'ac-maintenance-landlord-or-tenant',
    title: 'Who Pays for AC Maintenance in Abu Dhabi? Tenant vs Landlord',
    description: 'AC maintenance in Abu Dhabi rentals: what the leasing law says, what your Tawtheeq-registered contract may add, and what to do if the landlord won\'t repair.',
    eyebrow: 'Abu Dhabi · Renting guide',
    h1: 'AC Maintenance in Abu Dhabi Rentals: Who Pays?',
    lead: 'In an Abu Dhabi summer a failed AC is not a comfort issue, it is an emergency. Knowing whether the repair is yours or your landlord\'s - before it happens - saves days of back-and-forth.',
    datePublished: '2026-10-03',
    image: 'blog/living/aircon/aircon-thermostat.jpg',
    imageAlt: 'AC thermostat in a rented Abu Dhabi home',
    summary: {
        title: 'In short',
        items: [
            'Abu Dhabi\'s leasing law makes the landlord responsible for maintenance and major repairs unless the lease says otherwise.',
            'Leases often hand minor, routine upkeep to the tenant.',
            'Your lease is registered on Tawtheeq - read the maintenance clause before you sign.',
            'Disputes go to the Abu Dhabi rental disputes committee.',
        ],
    },
    sections: [
        {
            title: 'The legal starting point',
            paragraphs: [
                'Leasing in the emirate is governed by Abu Dhabi Law No. 20 of 2006 and its amendments. In general, the landlord must keep the property fit for use and carry out necessary maintenance and repairs, while the tenant must use the property properly and handle the minor upkeep that comes with day-to-day use. The lease can allocate responsibilities differently, which is why the contract wording matters.',
            ],
            note: 'General information only, not legal advice. Check your lease and seek advice for a specific dispute.',
        },
        {
            title: 'Villas vs apartments',
            cards: [
                { title: 'Villas', body: 'Leases for villas more often make the tenant responsible for routine servicing of the many AC units, sometimes with a cap per repair above which the landlord pays.' },
                { title: 'Apartments', body: 'In managed buildings, the landlord or building manager may arrange AC maintenance centrally, and district-cooled towers maintain the central plant themselves.' },
            ],
        },
        {
            title: 'A practical approach',
            steps: [
                { title: 'Before signing', body: 'Find the maintenance clause. Ask who handles AC servicing, what counts as "minor", and any value cap.' },
                { title: 'At move-in', body: 'Test every unit and record faults in the handover report.' },
                { title: 'During the lease', body: 'Do the upkeep that is yours and keep the reports.' },
                { title: 'When something breaks', body: 'Notify the landlord in writing with photos and a technician\'s diagnosis.' },
            ],
        },
        {
            title: 'If repairs are refused',
            paragraphs: [
                'Send a written request with a deadline. If the landlord still refuses a repair that is theirs under the lease and the law, you can bring a claim before the rental disputes committee. Keep paying rent while you do - withholding it can count against you.',
            ],
        },
    ],
    faqs: [
        {
            question: 'Is AC servicing the tenant\'s job in Abu Dhabi?',
            answer: 'Often, for routine servicing - but only if the lease says so. Otherwise maintenance generally rests with the landlord.',
        },
        {
            question: 'What is Tawtheeq?',
            answer: 'Abu Dhabi\'s system for registering tenancy contracts. Your registered lease is the document that sets out who maintains what.',
        },
    ],
    cta: {
        title: 'Get the fault in writing',
        body: (c) => `A diagnosis visit (${c.money(c.tables.repairDiagnosis)}) gives you the fault and the repair quote to send to your landlord.`,
        href: '/ae/ac-service-abu-dhabi/ac-repair',
        label: 'Book a diagnosis',
    },
}

export default content
